import * as React from 'react';
import {
  IProfileReportState,
  profileReportProvider,
} from '../../../../data/providers/reports/profileReportProvider';
import { Entity, EntityType } from '../../../../data/types';
import { Column } from '../../../layout/Column';
import { LinearLoading } from '../../../shared/loading/LinearLoading';
import { EPdfMode, isPdf, Pdf } from '../../../shared/pdf/Pdf';
import { PdfContainer } from '../../../shared/pdf/PdfContainer';
import { ErrorMessage } from '../../utils/ErrorMessages';
import { ReportOverview } from '../../utils/ReportOverview';
import { useFilename } from '../../utils/useFilename';
import { ProfileSettings } from '../settings/Settings';
import { GraphCard } from './GraphCard';
import { ResultList } from './ResultList';

interface IProps {
  pdfMode?: EPdfMode;
  entity: Entity;
  entityType: EntityType;
  onPdfModeChange: (pdfMode: EPdfMode) => void;
  loading: boolean;
  setLoading: (v: boolean) => void;
}

export const RadarReport = (props: IProps): React.ReactElement => {
  const [reportState, setReportState] = React.useState<IProfileReportState>();

  /**
   * @description subscribe to report state
   */
  React.useEffect((): (() => void) => {
    props.setLoading(true);
    const subscriber = profileReportProvider(
      props.entityType,
      props.entity.id
    ).subscribe((state: IProfileReportState): void => {
      setReportState(state);
      props.setLoading(false);
    });
    return (): void => subscriber.unsubscribe();
  }, [props.entity?.id]);

  /**
   * @description make filename for export
   */
  const filename = useFilename('profile', props.entity, reportState?.timeframe);

  return (
    <React.Fragment>
      {reportState?.isValid && !props.loading ? (
        isPdf(props.pdfMode) ? (
          <PdfContainer>
            <Pdf
              onComplete={() => {}}
              filename={filename}
              title='Profile Report'
              entity={props.entity}
              loading={props.loading}
              reportState={reportState}
              onPdfModeChange={props.onPdfModeChange}
              pdfMode={props.pdfMode}
            >
              <Column>
                <ReportOverview
                  populationTimeframe={{
                    dateType: reportState.comparisonPopulation?.dateRangeType,
                    dateRange:
                      reportState.comparisonPopulation?.dateRangeValues,
                  }}
                  populationEntityName={
                    reportState.comparisonPopulation?.entity?.label
                  }
                  targetTimeframe={{
                    dateType: reportState?.targetTimeframe?.dateType,
                    dateRange: reportState.targetTimeframe?.dateRange,
                  }}
                  targetEntity={props.entity}
                  reportName={`Profile Report - ${reportState?.selected?.name}`}
                  isPdf={isPdf(props.pdfMode)}
                />
                <GraphCard
                  pdfMode={props.pdfMode}
                  reportState={reportState}
                  entity={props.entity}
                />
                <ResultList
                  results={reportState.data}
                  pdfMode={props.pdfMode}
                  entity={props.entity}
                />
              </Column>
            </Pdf>
          </PdfContainer>
        ) : (
          <Column>
            {!props.loading ? (
              <React.Fragment>
                <ReportOverview
                  populationTimeframe={{
                    dateType: reportState.comparisonPopulation?.dateRangeType,
                    dateRange:
                      reportState.comparisonPopulation?.dateRangeValues,
                  }}
                  populationEntityName={
                    reportState.comparisonPopulation?.entity?.label
                  }
                  targetTimeframe={{
                    dateType: reportState?.targetTimeframe?.dateType,
                    dateRange: reportState.targetTimeframe?.dateRange,
                  }}
                  targetEntity={props.entity}
                  reportName={`Profile Report - ${reportState?.selected?.name}`}
                  isPdf={isPdf(props.pdfMode)}
                />
                <GraphCard
                  pdfMode={props.pdfMode}
                  reportState={reportState}
                  entity={props.entity}
                />
                <ResultList
                  results={reportState.data}
                  pdfMode={props.pdfMode}
                  entity={props.entity}
                />
              </React.Fragment>
            ) : (
              <LinearLoading />
            )}
          </Column>
        )
      ) : !props.loading ? (
        <Error
          errorCode={reportState?.errorCode}
          setLoading={props.setLoading}
          targetName={props.entity?.name}
          populationName={reportState?.comparisonPopulation?.entity?.label}
        />
      ) : (
        <LinearLoading />
      )}
    </React.Fragment>
  );
};

const Error = ({
  errorCode,
  setLoading,
  targetName,
  populationName,
}: {
  errorCode?: string;
  setLoading: (v: boolean) => void;
  targetName: string;
  populationName: string;
}): React.ReactElement => {
  const onSave = (): void => setLoading(true);
  switch (errorCode) {
    case 'missing-template':
      return (
        <ErrorMessage iconName='window' text='Please Select a Template'>
          <ProfileSettings
            style={{ marginTop: '10px', maxWidth: 'max-content' }}
            onSave={onSave}
          />
        </ErrorMessage>
      );
    case 'missing-metrics':
      return (
        <ErrorMessage iconName='poll' text='Missing Metrics'>
          <div style={{ marginTop: '10px' }}>
            Please select some metrics for this report in settings.
          </div>
          <ProfileSettings style={{ marginTop: '10px' }} onSave={onSave} />
        </ErrorMessage>
      );

    case 'target-insufficient-data':
      return (
        <ErrorMessage iconName='error_outline' text='Not Enough Data'>
          <div style={{ marginTop: '10px' }}>
            {targetName} does not have enough data during the selected time
            period to produce this report. Try changing either the timeframe or
            choose different metrics.
          </div>
          <ProfileSettings style={{ marginTop: '10px' }} onSave={onSave} />
        </ErrorMessage>
      );

    case 'population-insufficient-data':
      return (
        <ErrorMessage iconName='error_outline' text='Not Enough Data'>
          <div style={{ marginTop: '10px' }}>
            {populationName} does not have enough data during the selected time
            period to produce this report. Try changing either the timeframe or
            choose different metrics.
          </div>
          <ProfileSettings style={{ marginTop: '10px' }} onSave={onSave} />
        </ErrorMessage>
      );

    default:
      return null;
  }
};
